import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

/**
 * ModalHabilitationsOperateur
 * Gère les habilitations individuelles d'un opérateur
 * Par défaut, l'opérateur hérite des habilitations de son profil
 * Ce modal permet de personnaliser (override) pour chaque opérateur
 */
function ModalHabilitationsOperateur({ operateur, onClose, onUpdate }) {
  const [operations, setOperations] = useState([]);
  const [habilitations, setHabilitations] = useState([]);
  const [hasCustomHabilitations, setHasCustomHabilitations] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Charger les données au montage
  useEffect(() => {
    if (operateur) {
      loadData();
    }
  }, [operateur]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      
      // 1. Récupérer toutes les opérations
      const { data: opsData, error: opsError } = await supabase
        .from('operations')
        .select('id, nom, type_produit_id, types_produits(nom)')
        .order('types_produits(nom)', { ascending: true })
        .order('nom', { ascending: true });
      
      if (opsError) throw opsError;
      setOperations(opsData || []);
      
      // 2. Vérifier si l'opérateur a des habilitations individuelles
      const { data: customData, error: customError } = await supabase
        .from('operateur_operations')
        .select('operation_id')
        .eq('operateur_id', operateur.id);
      
      if (customError) throw customError;
      
      const hasCustom = customData && customData.length > 0;
      setHasCustomHabilitations(hasCustom);
      
      if (hasCustom) {
        // Mode personnalisé : charger les habilitations individuelles
        setHabilitations(customData.map(h => h.operation_id));
      } else {
        // Mode par défaut : charger les habilitations du profil
        const { data: profilData, error: profilError } = await supabase
          .from('profil_operations')
          .select('operation_id')
          .eq('profil_id', operateur.profil_id);
        
        if (profilError) throw profilError;
        setHabilitations(profilData?.map(h => h.operation_id) || []);
      }
      
    } catch (error) {
      console.error('Erreur chargement habilitations opérateur:', error);
      alert('Erreur lors du chargement des habilitations');
    } finally {
      setLoading(false);
    }
  };
  
  // Basculer en mode personnalisé
  const handleActiverPersonnalisation = async () => {
    try {
      setSaving(true);
      
      // Appeler la fonction SQL pour copier les habilitations du profil
      const { error } = await supabase
        .rpc('initialiser_habilitations_operateur', {
          p_operateur_id: operateur.id
        });
      
      if (error) throw error;
      
      setHasCustomHabilitations(true);
      await loadData(); // Recharger
      
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('Erreur activation personnalisation:', error);
      alert('Erreur lors de l\'activation de la personnalisation');
    } finally {
      setSaving(false);
    }
  };
  
  // Revenir au mode profil par défaut
  const handleReinitialiserProfil = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir revenir aux habilitations du profil ?\nToutes les personnalisations seront perdues.')) {
      return;
    }
    
    try {
      setSaving(true);
      
      // Appeler la fonction SQL pour supprimer les habilitations individuelles
      const { error } = await supabase
        .rpc('reinitialiser_habilitations_operateur', {
          p_operateur_id: operateur.id
        });
      
      if (error) throw error;
      
      setHasCustomHabilitations(false);
      await loadData(); // Recharger
      
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('Erreur réinitialisation profil:', error);
      alert('Erreur lors de la réinitialisation');
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle une habilitation (uniquement en mode personnalisé)
  const handleToggle = async (operationId) => {
    if (!hasCustomHabilitations) {
      alert('Activez d\'abord la personnalisation pour modifier les habilitations');
      return;
    }
    
    try {
      const isHabilite = habilitations.includes(operationId);
      
      if (isHabilite) {
        // Retirer l'habilitation
        const { error } = await supabase
          .from('operateur_operations')
          .delete()
          .eq('operateur_id', operateur.id)
          .eq('operation_id', operationId);
        
        if (error) throw error;
        
        setHabilitations(habilitations.filter(id => id !== operationId));
      } else {
        // Ajouter l'habilitation
        const { error } = await supabase
          .from('operateur_operations')
          .insert({
            operateur_id: operateur.id,
            operation_id: operationId
          });
        
        if (error) throw error;
        
        setHabilitations([...habilitations, operationId]);
      }
      
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('Erreur toggle habilitation:', error);
      alert('Erreur lors de la modification');
    }
  };
  
  // Grouper les opérations par type de produit
  const operationsGroupees = operations.reduce((acc, op) => {
    const produitNom = op.types_produits?.nom || 'Sans produit';
    if (!acc[produitNom]) {
      acc[produitNom] = [];
    }
    acc[produitNom].push(op);
    return acc;
  }, {});
  
  if (!operateur) return null;
  
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            🔐 Habilitations de {operateur.prenom} {operateur.nom}
          </h2>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        
        <div style={styles.content}>
          {loading ? (
            <div style={styles.loading}>Chargement...</div>
          ) : (
            <>
              {/* Bandeau d'information */}
              <div style={hasCustomHabilitations ? styles.infoCustom : styles.infoDefault}>
                {hasCustomHabilitations ? (
                  <>
                    <strong>🎯 Mode personnalisé</strong>
                    <p style={styles.infoText}>
                      Les habilitations sont personnalisées pour cet opérateur.
                      <br/>
                      <strong>{habilitations.length}</strong> opération(s) autorisée(s).
                    </p>
                    <button 
                      style={styles.btnReset}
                      onClick={handleReinitialiserProfil}
                      disabled={saving}
                    >
                      ↺ Revenir au profil
                    </button>
                  </>
                ) : (
                  <>
                    <strong>👥 Mode profil (par défaut)</strong>
                    <p style={styles.infoText}>
                      Profil : <strong>{operateur.profils?.nom || 'Non assigné'}</strong>
                      <br/>
                      <strong>{habilitations.length}</strong> opération(s) héritée(s) du profil.
                    </p>
                    <button 
                      style={styles.btnCustomize}
                      onClick={handleActiverPersonnalisation}
                      disabled={saving}
                    >
                      ✏️ Personnaliser
                    </button>
                  </>
                )}
              </div>
              
              {/* Liste des opérations par produit */}
              <div style={styles.operations}>
                {Object.entries(operationsGroupees).map(([produitNom, ops]) => (
                  <div key={produitNom} style={styles.produitGroup}>
                    <h3 style={styles.produitTitle}>{produitNom}</h3>
                    
                    <div style={styles.operationsList}>
                      {ops.map(op => {
                        const isHabilite = habilitations.includes(op.id);
                        
                        return (
                          <label 
                            key={op.id} 
                            style={{
                              ...styles.operationItem,
                              opacity: hasCustomHabilitations ? 1 : 0.7,
                              cursor: hasCustomHabilitations ? 'pointer' : 'not-allowed'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isHabilite}
                              onChange={() => handleToggle(op.id)}
                              disabled={!hasCustomHabilitations}
                              style={styles.checkbox}
                            />
                            <span style={isHabilite ? styles.operationNomActif : styles.operationNom}>
                              {op.nom}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        
        <div style={styles.footer}>
          <button style={styles.btnClose} onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '2px solid #e0e0e0',
  },
  title: {
    margin: 0,
    fontSize: '22px',
    color: '#333',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
    padding: '4px 8px',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '24px',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '18px',
    color: '#666',
  },
  infoDefault: {
    backgroundColor: '#e3f2fd',
    border: '2px solid #2196f3',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
  },
  infoCustom: {
    backgroundColor: '#fff3e0',
    border: '2px solid #ff9800',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
  },
  infoText: {
    margin: '8px 0',
    fontSize: '14px',
  },
  btnCustomize: {
    marginTop: '12px',
    padding: '8px 16px',
    backgroundColor: '#ff9800',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  btnReset: {
    marginTop: '12px',
    padding: '8px 16px',
    backgroundColor: '#2196f3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
  },
  operations: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  produitGroup: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '16px',
  },
  produitTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    color: '#555',
    fontWeight: 'bold',
  },
  operationsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  operationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  operationNom: {
    fontSize: '14px',
    color: '#666',
  },
  operationNomActif: {
    fontSize: '14px',
    color: '#333',
    fontWeight: '500',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '16px 24px',
    borderTop: '2px solid #e0e0e0',
  },
  btnClose: {
    padding: '10px 24px',
    backgroundColor: '#666',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
};

export default ModalHabilitationsOperateur;
